import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bot,
  Play,
  Square,
  RefreshCw,
  RotateCcw,
  Calendar,
  User,
  ExternalLink,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDiscordAvatarUrl } from "@/utils/discord";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  adminCustomBotDetailQueryOptions,
  useAdminBotAction,
} from "@/lib/api/admin";
import { usePageTitle } from "@/hooks/use-page-title";
import { toast } from "sonner";
import type { DeploymentStatus } from "@/lib/api/types";

export const Route = createFileRoute(
  "/dashboard/admin/custom-bots/$applicationId"
)({
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(
      adminCustomBotDetailQueryOptions(params.applicationId)
    ),
  component: AdminCustomBotDetailPage,
});

const statusColors: Record<DeploymentStatus, string> = {
  running: "bg-emerald-500/10 text-emerald-500",
  pending: "bg-yellow-500/10 text-yellow-500",
  deploying: "bg-blue-500/10 text-blue-500",
  stopped: "bg-gray-500/10 text-gray-500",
  failed: "bg-red-500/10 text-red-500",
  suspended: "bg-orange-500/10 text-orange-500",
};

function AdminCustomBotDetailPage() {
  const { applicationId } = Route.useParams();
  const { data: bot } = useSuspenseQuery(
    adminCustomBotDetailQueryOptions(applicationId)
  );
  const botAction = useAdminBotAction(applicationId);
  usePageTitle(`Admin - ${bot?.name ?? "Bot"}`);

  if (!bot) return null;

  const status = bot.deployment?.status;
  const isRunning = status === "running";
  const isStopped =
    status === "stopped" || status === "failed" || status === "suspended";

  function handleAction(action: "start" | "stop" | "restart" | "redeploy") {
    botAction.mutate(action, {
      onSuccess: () => toast.success(`Bot ${action} initiated`),
      onError: (err) => toast.error(err.message),
    });
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="gap-2">
        <Link to="/dashboard/admin/custom-bots">
          <ArrowLeft className="size-4" />
          Back to Custom Bots
        </Link>
      </Button>

      {/* Header */}
      <Card>
        <CardContent className="space-y-4 py-6">
          <div className="flex items-center gap-4">
            <Avatar className="size-16">
              {bot.discordBotAvatar && bot.discordBotId ? (
                <AvatarImage
                  src={getDiscordAvatarUrl(
                    bot.discordBotId!,
                    bot.discordBotAvatar
                  )}
                />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-rose-500/10 to-red-500/10 text-lg">
                <Bot className="size-8" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-xl font-semibold">{bot.name}</h1>
                {status && (
                  <Badge
                    className={statusColors[status] ?? ""}
                    variant="secondary"
                  >
                    {status}
                  </Badge>
                )}
              </div>
              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {bot.discordBotUsername && (
                  <span>@{bot.discordBotUsername}</span>
                )}
                {bot.owner && (
                  <span className="flex items-center gap-1">
                    <User className="size-3.5" />
                    {bot.owner.tenantName}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  {new Date(bot.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div className="border-border flex flex-wrap items-center gap-2 border-t pt-4">
            {isStopped && (
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={() => handleAction("start")}
                disabled={botAction.isPending}
              >
                <Play className="size-4" />
                Start
              </Button>
            )}
            {isRunning && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  onClick={() => handleAction("restart")}
                  disabled={botAction.isPending}
                >
                  <RefreshCw className="size-4" />
                  Restart
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  onClick={() => handleAction("stop")}
                  disabled={botAction.isPending}
                >
                  <Square className="size-4" />
                  Stop
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={() => handleAction("redeploy")}
              disabled={botAction.isPending}
            >
              <RotateCcw className="size-4" />
              Redeploy
            </Button>
            {bot.dokployUrl && (
              <>
                <div className="bg-border mx-1 h-5 w-px" />
                <Button size="sm" variant="outline" className="gap-2" asChild>
                  <a
                    href={bot.dokployUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="size-4" />
                    Dokploy
                  </a>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Deployment Info */}
      {bot.deployment && (
        <Card>
          <CardHeader>
            <CardTitle>Deployment Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge
                className={statusColors[bot.deployment.status] ?? ""}
                variant="secondary"
              >
                {bot.deployment.status}
              </Badge>
            </div>
            {bot.deployment.statusMessage && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Message</span>
                <span>{bot.deployment.statusMessage}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created</span>
              <span>{new Date(bot.deployment.createdAt).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Updated</span>
              <span>{new Date(bot.deployment.updatedAt).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deployment Logs */}
      {bot.deploymentLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Deployment Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bot.deploymentLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          log.status === "done"
                            ? "bg-emerald-500/10 text-emerald-500"
                            : log.status === "error"
                              ? "bg-red-500/10 text-red-500"
                              : "bg-blue-500/10 text-blue-500"
                        }
                      >
                        {log.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.title ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(log.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
